import React from "react";
import {
    makeEmptyReferenceDraft,
    QBankReferenceDraft,
    QBankReferenceType,
} from "../utils/qbankReferences";
import styles from "./QBankReferenceEditor.module.css";

interface QBankReferenceEditorProps {
    references: QBankReferenceDraft[];
    onChange: (references: QBankReferenceDraft[]) => void;
    disabled?: boolean;
}

type ReferenceField = keyof Pick<
    QBankReferenceDraft,
    | "text_excerpt"
    | "source_name"
    | "type"
    | "page_number"
    | "journal"
    | "first_author"
    | "date_of_publication"
    | "doi"
>;

const QBankReferenceEditor: React.FC<QBankReferenceEditorProps> = ({
    references,
    onChange,
    disabled = false,
}) => {
    const updateReference = (
        index: number,
        field: ReferenceField,
        value: string
    ) => {
        onChange(references.map((reference, idx) => (
            idx === index
                ? {
                    ...reference,
                    [field]: field === "type" ? (value as QBankReferenceType) : value,
                }
                : reference
        )));
    };

    const addReference = () => {
        onChange([...references, makeEmptyReferenceDraft()]);
    };

    const removeReference = (index: number) => {
        onChange(references.filter((_, idx) => idx !== index));
    };

    const moveReference = (index: number, direction: -1 | 1) => {
        const nextIndex = index + direction;
        if (nextIndex < 0 || nextIndex >= references.length) return;

        const nextReferences = [...references];
        const [reference] = nextReferences.splice(index, 1);
        nextReferences.splice(nextIndex, 0, reference);
        onChange(nextReferences);
    };

    return (
        <div className={styles.referenceEditor}>
            {references.length === 0 ? (
                <p className={styles.emptyState}>No references.</p>
            ) : (
                references.map((reference, index) => (
                    <div key={index} className={styles.referenceCard}>
                        <div className={styles.referenceHeader}>
                            <span>Reference {index + 1}</span>
                            <div className={styles.referenceActions}>
                                <button
                                    type="button"
                                    onClick={() => moveReference(index, -1)}
                                    disabled={disabled || index === 0}
                                >
                                    Up
                                </button>
                                <button
                                    type="button"
                                    onClick={() => moveReference(index, 1)}
                                    disabled={disabled || index === references.length - 1}
                                >
                                    Down
                                </button>
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() => removeReference(index)}
                                    disabled={disabled}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>

                        <div className={styles.formGrid}>
                            <label className={styles.fullWidth}>
                                <span>Excerpt</span>
                                <textarea
                                    value={reference.text_excerpt}
                                    onChange={(event) => updateReference(index, "text_excerpt", event.target.value)}
                                    rows={3}
                                    disabled={disabled}
                                />
                            </label>

                            <label className={styles.fullWidth}>
                                <span>Source Name</span>
                                <input
                                    type="text"
                                    value={reference.source_name}
                                    onChange={(event) => updateReference(index, "source_name", event.target.value)}
                                    disabled={disabled}
                                />
                            </label>

                            <label>
                                <span>Type</span>
                                <select
                                    value={reference.type}
                                    onChange={(event) => updateReference(index, "type", event.target.value)}
                                    disabled={disabled}
                                >
                                    <option value="textbook">Textbook</option>
                                    <option value="article">Article</option>
                                </select>
                            </label>

                            <label>
                                <span>Page</span>
                                <input
                                    type="number"
                                    value={reference.page_number}
                                    onChange={(event) => updateReference(index, "page_number", event.target.value)}
                                    disabled={disabled}
                                />
                            </label>

                            <label>
                                <span>Journal</span>
                                <input
                                    type="text"
                                    value={reference.journal}
                                    onChange={(event) => updateReference(index, "journal", event.target.value)}
                                    disabled={disabled}
                                />
                            </label>

                            <label>
                                <span>First Author</span>
                                <input
                                    type="text"
                                    value={reference.first_author}
                                    onChange={(event) => updateReference(index, "first_author", event.target.value)}
                                    disabled={disabled}
                                />
                            </label>

                            <label>
                                <span>Publication Date</span>
                                <input
                                    type="text"
                                    value={reference.date_of_publication}
                                    onChange={(event) => updateReference(index, "date_of_publication", event.target.value)}
                                    disabled={disabled}
                                />
                            </label>

                            <label>
                                <span>DOI</span>
                                <input
                                    type="text"
                                    value={reference.doi}
                                    onChange={(event) => updateReference(index, "doi", event.target.value)}
                                    disabled={disabled}
                                />
                            </label>
                        </div>
                    </div>
                ))
            )}

            <button
                type="button"
                className={styles.addButton}
                onClick={addReference}
                disabled={disabled}
            >
                Add Reference
            </button>
        </div>
    );
};

export default QBankReferenceEditor;
